import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { getLocalizedInstrumentOptions, setUseSoundfonts } from '../../utils/instrumentFactory';
import { preloadCommonInstruments, boostSystemVolume } from '../../utils/soundfontInstruments';
import { getLocalizedPartName } from '../../utils/voicePartNames';
import { logActivity } from '../../utils/activityLogger';
import ScratchRecorder from '../ScratchRecorder';
import OffScoreTipsDisplay from '../OffScoreTipsDisplay';
import SongVideos from '../SongVideos';
import { useOfficialPdfUrl } from './useOfficialPdf';
import strings from './strings';
import { Group, Row, Switch, Stepper, Range, Select, Button, IconToggle } from './panelWidgets';
import { GRADE_STYLES, SCOOP_COLOR, SCOOP_UP } from '../desktop/gradeOverlay';
import { describePass } from '../desktop/useGradedTakes';
import '../../styles/practice-panel.css';

const LATENCY_STEP_MS = 10;
const LATENCY_LIMIT_MS = 1000;

/**
 * Everything that is not on the dock, as collapsible groups of consistent
 * rows: the lower part of the desktop control panel and the body of the
 * phone More sheet. `locked` (playing or paused) disables anything that
 * rebuilds playback; live-safe controls stay enabled. `phone` leaves out what needs a mouse or a desktop
 * (hover to hear, the recorder) and gets touch-sized widgets from CSS.
 * `grading` and `recorder` (desktop) are useGradedTakes' and
 * useScratchRecorder's state for the Record group; `onOpenMonitor` opens
 * the pitch monitor (null while playing, undefined where there is none);
 * `devices` (useAudioDevices) picks the mic and the output. `lyrics` is
 * usePracticeShell's lyrics state for the Display group.
 */
const PanelGroups = ({ controls: c, locked, phone = false, partsOverride = false, onPartsOverride, stepHints = false, onStepHints, stepHintsAvailable = false, lyrics = null, grading = null, recorder = null, onOpenMonitor = undefined, devices = null }) => {
  const { t } = useLanguage();

  // Sampled sounds on/off, remembered per device
  const [soundfonts, setSoundfonts] = useState(() => {
    const saved = localStorage.getItem('musicxml-use-soundfonts');
    const enabled = saved !== null ? saved === 'true' : true;
    setUseSoundfonts(enabled);
    return enabled;
  });
  const [soundfontsLoading, setSoundfontsLoading] = useState(false);
  const toggleSoundfonts = async (enabled) => {
    setSoundfonts(enabled);
    setUseSoundfonts(enabled);
    localStorage.setItem('musicxml-use-soundfonts', String(enabled));
    if (enabled) {
      boostSystemVolume();
      setSoundfontsLoading(true);
      try { await preloadCommonInstruments(); } catch (e) { /* ignore */ } finally { setSoundfontsLoading(false); }
    }
  };
  useEffect(() => {
    if (soundfonts) {
      setSoundfontsLoading(true);
      preloadCommonInstruments().catch(() => {}).finally(() => setSoundfontsLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const officialPdfUrl = useOfficialPdfUrl(c.fileName);
  const openOfficialPdf = () => {
    logActivity({
      user: c.user,
      actionType: 'song_pdf_download',
      resourceType: 'cloud',
      resourceId: c.fileName,
      description: `User downloaded official PDF for: ${c.fileName}`,
      metadata: { ui: phone ? 'mobile_shell' : 'desktop_shell' }
    });
    window.open(officialPdfUrl, '_blank', 'noopener');
  };

  const autoSwingLabel = c.detectedSwingInfo?.swingRatio != null
    ? `${c.detectedSwingInfo.swingFirst}:${c.detectedSwingInfo.swingSecond}`
    : '5:3';
  const transposeLabel = c.transposeSteps > 0 ? `+${c.transposeSteps}` : String(c.transposeSteps);

  // Highlight sync is stored in seconds; the panel works in whole milliseconds
  const latencyMs = Math.round((c.audioLatency || 0) * 1000);
  const setLatencyMs = (ms) => {
    const clamped = Math.max(-LATENCY_LIMIT_MS, Math.min(LATENCY_LIMIT_MS, ms));
    c.setAudioLatency(clamped / 1000);
  };

  const divisiAnalysis = c.osmdRef?.current?._divisiAnalysis;
  const divisiFor = (partId) => (divisiAnalysis ? divisiAnalysis.instances.filter(d => String(d.partId) === String(partId)) : []);
  let lyricsHint = null;
  if (lyrics) {
    if (!lyrics.available) lyricsHint = strings.lyricsNone;
    else if (lyrics.followsMe) lyricsHint = strings.lyricsHint;
    else lyricsHint = `${strings.lyricsWordsOf} ${lyrics.partName || 'the melody'}${lyrics.mineHasNone ? ` (${strings.lyricsMineHasNone})` : ''}`;
  }

  const hearingAll = c.partControls.every(p => p.checked);
  const hearingOnlyMe = c.selectedPart != null && c.partControls.every(p => p.checked === (p.id === c.selectedPart));
  const setHearAll = () => c.partControls.forEach(p => { if (!p.checked) c.onPartToggle(p.id, true); });
  const setHearOnlyMe = () => c.partControls.forEach(p => { const on = p.id === c.selectedPart; if (p.checked !== on) c.onPartToggle(p.id, on); });

  return (
    <div className="dps-groups-list">
      {locked && <p className="mps-hint mps-hint-locked">{strings.stopToChange}</p>}

      <Group id="sound" title={strings.groupSound}>
        <Row label={t('audio.sound')}>
          <Select value={c.instrument} onChange={c.setInstrument} disabled={c.isLoading} label={t('audio.sound')}>
            {getLocalizedInstrumentOptions(t).map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Select>
        </Row>
        <Row label="Sampled sounds" hint={soundfontsLoading ? t('audio.loadingSoundfonts') : undefined}>
          <Switch checked={soundfonts} onChange={toggleSoundfonts} disabled={soundfontsLoading} label="Sampled sounds" />
        </Row>
        <Row label={t('audio.masterVolume')} stacked>
          <Range value={c.volume} min={-40} max={6} step={1} onChange={c.setVolume} format={(v) => `${v} dB`} label={t('audio.masterVolume')} />
        </Row>
        <Row label="Velocity" hint="how hard each note is struck" stacked>
          <Range value={c.velocity} min={0} max={1} step={0.01} onChange={c.setVelocity} format={(v) => `${Math.round(v * 100)}%`} label="Velocity" />
        </Row>
        <Row label={strings.transpose}>
          <Stepper
            display={transposeLabel}
            decLabel="♭"
            incLabel="♯"
            decTitle="Transpose down"
            incTitle="Transpose up"
            disabled={c.isLoading || locked}
            canDec={c.transposeSteps > -11}
            canInc={c.transposeSteps < 11}
            onDec={() => c.onTransposeChange(c.transposeSteps - 1)}
            onInc={() => c.onTransposeChange(c.transposeSteps + 1)}
            onReset={c.transposeSteps !== 0 ? () => c.onTransposeChange(0) : undefined}
          />
        </Row>
      </Group>
      <Group id="parts" title={strings.groupParts} badge={partsOverride ? 'override' : (c.partControls.length ? String(c.partControls.length) : undefined)}>
        <Row label={strings.partsOverride} hint={partsOverride ? strings.partsOverrideOn : strings.partsOverrideOff}>
          <Switch checked={partsOverride} onChange={onPartsOverride} label={strings.partsOverride} />
        </Row>
        <div className={`dps-parts-bulk ${partsOverride ? '' : 'dps-dimmed'}`}>
          <span className="dps-row-label"><span>Hear</span></span>
          <div className="dps-seg">
            <button type="button" className={hearingAll ? 'active' : ''} disabled={!partsOverride} onClick={setHearAll}>All</button>
            <button type="button" className={hearingOnlyMe ? 'active' : ''} disabled={!partsOverride || c.selectedPart == null} onClick={setHearOnlyMe}>Only me</button>
          </div>
        </div>
        <div className="dps-parts">
          <div className="dps-parts-head">
            <span>Part</span><span title="Hear">🔊</span><span title="Show">👁</span><span title="This is me">Me</span><span>Volume</span>
          </div>
          {c.partControls.map(part => {
            const divisi = divisiFor(part.id);
            const isMe = c.selectedPart === part.id;
            return (
              <React.Fragment key={part.id}>
                <div className={`dps-part ${isMe ? 'me' : ''}`}>
                  <span className="dps-part-name" title={part.name}>{getLocalizedPartName(part.name, t)}</span>
                  <IconToggle checked={part.checked} disabled={!partsOverride} onChange={(v) => c.onPartToggle(part.id, v)} label={`Hear ${part.name}`}>🔊</IconToggle>
                  <IconToggle checked={part.visible !== false} disabled={!partsOverride} onChange={(v) => c.onPartVisibilityToggle(part.id, v)} label={`Show ${part.name}`}>👁</IconToggle>
                  <button type="button" className={`dps-me ${isMe ? 'on' : ''}`} aria-pressed={isMe} onClick={() => c.onSelectedPartChange(part.id)} title="This is my part">Me</button>
                  <Range value={c.getPartVolume(part.id)} min={0} max={100} step={1} disabled={!partsOverride} onChange={(v) => c.setPartVolume(part.id, v)} format={(v) => `${v}%`} label={`${part.name} volume`} />
                </div>
                {divisi.length > 0 && (
                  <div className={`dps-part-divisi ${partsOverride ? '' : 'dps-dimmed'}`}>
                    <span className="dps-part-divisi-label">Divisi ({divisi.length})</span>
                    <label><input type="checkbox" checked={part.upperVoice !== false} onChange={(e) => c.onUpperVoiceToggle(part.id, e.target.checked)} /> Upper</label>
                    <label><input type="checkbox" checked={part.lowerVoice !== false} onChange={(e) => c.onLowerVoiceToggle(part.id, e.target.checked)} /> Lower</label>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </Group>
      <Group id="song" title={strings.groupSong}>
        <div className="dps-actions">
          <Button onClick={c.onExportPdf} disabled={c.isLoading || !c.hasLoadedFile || c.isGeneratingPdf}>{c.isGeneratingPdf ? '⏳' : '🖨️'} {strings.pdf}</Button>
          {officialPdfUrl && <Button onClick={openOfficialPdf}>📄 {strings.pdfOfficial}</Button>}
          <Button onClick={c.onExportMp3} disabled={c.isLoading || !c.hasLoadedFile || c.isRecordingMp3 || c.isRenderingMp3 || locked || c.isLoopMode}>
            {c.isRenderingMp3 ? '⏳' : (c.isRecordingMp3 ? '⏺' : '🎵')} {strings.mp3}
          </Button>
          <Button onClick={c.onResetSettings} disabled={c.isLoading || !c.hasLoadedFile || locked}>🔄 {strings.resetSettings}</Button>
        </div>
        {c.user && c.hasLoadedFile && (
          <div className="dps-song-extras">
            <OffScoreTipsDisplay musicxmlId={c.fileName} isCollapsed={true} refreshTrigger={c.tipRefreshTrigger} />
            <SongVideos videos={c.songVideos} onVideoStart={c.onVideoStart} />
          </div>
        )}
      </Group>
      {!phone && (
        <Group id="record" title="Record" badge={grading?.enabled ? 'grading' : undefined}>
          {grading && (
            <div className="dps-grading">
              <Row label={strings.grade} hint={grading.available ? undefined : strings.gradeNeedMe} tip={grading.enabled ? strings.gradeOn : strings.gradeHint}>
                <Switch
                  checked={grading.enabled}
                  disabled={!grading.available || grading.starting || locked || Boolean(c.hoverPlay)}
                  onChange={grading.setEnabled}
                  label={strings.grade}
                />
              </Row>
              {grading.error && <p className="dps-hint dps-grade-error">{grading.error}</p>}
              {grading.enabled && (
                <Row label={strings.gradeLive} tip={grading.live ? strings.gradeLiveOn : strings.gradeLiveOff}>
                  <Switch checked={grading.live} disabled={locked} onChange={grading.setLive} label={strings.gradeLive} />
                </Row>
              )}
              {(grading.enabled || grading.hasGrades) && (
                <Row label={strings.gradeTuning} tip={grading.tuning === 'just' ? strings.gradeJustHint : strings.gradeEqualHint}>
                  <div className="dps-seg">
                    <button type="button" className={grading.tuning === 'equal' ? 'active' : ''} onClick={() => grading.setTuning('equal')}>{strings.gradeEqual}</button>
                    <button type="button" className={grading.tuning === 'just' ? 'active' : ''} onClick={() => grading.setTuning('just')}>{strings.gradeJust}</button>
                  </div>
                </Row>
              )}
              {grading.passes.length > 0 && (
                <ul className="dps-grade-passes">
                  {grading.passes.slice(-4).reverse().map((summary) => {
                    const { headline, detail } = describePass(summary, grading.tuning);
                    return (
                      <li key={summary.pass}>
                        <span>{headline}</span>
                        {detail && <small>{detail}</small>}
                      </li>
                    );
                  })}
                </ul>
              )}
              {(grading.hasGrades || grading.hasTake) && (
                <div className="dps-actions">
                  {grading.hasGrades && (
                    <>
                      <Button onClick={() => grading.setVisible(!grading.visible)}>{grading.visible ? strings.gradeHide : strings.gradeShow}</Button>
                      <Button onClick={grading.clear} disabled={grading.listening}>{strings.gradeClear}</Button>
                    </>
                  )}
                  {/* A take stopped early can be saved even before any note was graded */}
                  {grading.hasTake && (
                    <>
                      <Button
                        onClick={grading.hearing === 'recording' ? grading.stopHearing : grading.playRecording}
                        disabled={grading.listening}
                        title={grading.playsSection ? strings.gradePlaySectionTitle : strings.gradePlayTitle}
                      >
                        {grading.hearing === 'recording' ? `■ ${strings.gradePlayStop}` : `▶ ${grading.playsSection ? strings.gradePlaySection : strings.gradePlay}`}
                      </Button>
                      <Button onClick={grading.downloadTake} disabled={grading.listening} title={strings.gradeDownloadTitle}>⬇ {strings.gradeDownload}</Button>
                    </>
                  )}
                </div>
              )}
              {(grading.enabled || grading.hasGrades) && (
                <div className="dps-grade-legend">
                  {Object.entries(GRADE_STYLES).map(([grade, style]) => (
                    <span key={grade}>
                      <i style={{ borderColor: style.color, borderStyle: style.dashed ? 'dashed' : 'solid' }} />
                      {style.label}
                    </span>
                  ))}
                  <span><b style={{ color: SCOOP_COLOR }}>{SCOOP_UP}</b>scooped into the note</span>
                </div>
              )}
            </div>
          )}
          {onOpenMonitor !== undefined && (
            <div className="dps-grading">
              <Row label={strings.monitor} hint={onOpenMonitor ? undefined : strings.stopToChange} tip={strings.monitorHint}>
                <Button onClick={onOpenMonitor} disabled={!onOpenMonitor}>{'🎚'} {strings.monitorOpen}</Button>
              </Row>
            </div>
          )}
          {recorder && (
            <>
              <p className="dps-recorder-title" title={strings.recorderTip}>{strings.recorder}<span className="dps-row-tip" aria-hidden="true"> ⓘ</span></p>
              <div className="dps-recorder">
                <ScratchRecorder recorder={recorder} />
              </div>
            </>
          )}
          {devices && (
            <div className="dps-grading dps-devices">
              <Row
                label={strings.deviceInput}
                hint={!devices.named ? strings.devicesUnnamed : (devices.windows ? strings.deviceInputWindows : undefined)}
                tip={devices.windows ? `${strings.deviceInputTip} ${strings.deviceInputWindowsTip}` : strings.deviceInputTip}
              >
                <Select value={devices.input} onChange={devices.setInput} label={strings.deviceInput}>
                  <option value="">{strings.deviceDefault}</option>
                  {devices.inputs.map((d, i) => <option key={d.id} value={d.id}>{d.label || `${strings.deviceInput} ${i + 1}`}</option>)}
                </Select>
              </Row>
              {devices.canChooseOutput && (
                <Row label={strings.deviceOutput} tip={strings.deviceOutputTip}>
                  <Select value={devices.output} onChange={devices.setOutput} label={strings.deviceOutput}>
                    <option value="">{strings.deviceDefault}</option>
                    {devices.outputs.map((d, i) => <option key={d.id} value={d.id}>{d.label || `${strings.deviceOutput} ${i + 1}`}</option>)}
                  </Select>
                </Row>
              )}
            </div>
          )}
        </Group>
      )}
      <Group id="display" title={strings.groupDisplay} defaultOpen={false}>
        {lyrics && (
          <Row label={strings.lyrics} hint={lyricsHint}>
            <Switch checked={lyrics.on && lyrics.available} disabled={!lyrics.available} onChange={lyrics.setOn} label={strings.lyrics} />
          </Row>
        )}
        {lyrics && lyrics.on && lyrics.available && (
          <Row label={strings.lyricsMode} tip={strings.lyricsModeTip}>
            <div className="dps-seg">
              <button type="button" className={lyrics.mode === 'word' ? 'active' : ''} onClick={() => lyrics.setMode('word')}>{strings.lyricsWord}</button>
              <button type="button" className={lyrics.mode === 'phrase' ? 'active' : ''} onClick={() => lyrics.setMode('phrase')}>{strings.lyricsPhrase}</button>
              <button type="button" className={lyrics.mode === 'magnify' ? 'active' : ''} onClick={() => lyrics.setMode('magnify')}>{strings.lyricsMagnify}</button>
            </div>
          </Row>
        )}
        {lyrics && lyrics.on && lyrics.verses.length > 1 && (
          <Row label={strings.lyricsVerse}>
            <Select value={lyrics.verse} onChange={lyrics.setVerse} label={strings.lyricsVerse}>
              <option value="auto">{strings.lyricsVerseAuto}</option>
              {lyrics.verses.map(verse => <option key={verse} value={verse}>{verse}</option>)}
            </Select>
          </Row>
        )}
        {onStepHints && (
          <Row label={strings.stepHints} hint={stepHintsAvailable ? strings.stepHintsHint : strings.stepHintsNeedMe}>
            <Switch checked={stepHints && stepHintsAvailable} disabled={!stepHintsAvailable} onChange={onStepHints} label={strings.stepHints} />
          </Row>
        )}
        <Row label={t('audio.autoScroll')}>
          <Switch checked={Boolean(c.autoScroll)} onChange={c.setAutoScroll} label={t('audio.autoScroll')} />
        </Row>
        <Row label={t('audio.lookahead')} hint="beats">
          <Stepper
            display={String(c.lookaheadBeats)}
            decTitle="Fewer beats"
            incTitle="More beats"
            canDec={c.lookaheadBeats > 0}
            canInc={c.lookaheadBeats < 16}
            onDec={() => c.setLookaheadBeats(Math.max(0, c.lookaheadBeats - 1))}
            onInc={() => c.setLookaheadBeats(Math.min(16, c.lookaheadBeats + 1))}
          />
        </Row>
        <Row label={strings.highlightSync} hint={strings.highlightSyncHint}>
          <Stepper
            display={latencyMs > 0 ? `+${latencyMs}` : String(latencyMs)}
            decTitle="Highlight earlier"
            incTitle="Highlight later"
            canDec={latencyMs > -LATENCY_LIMIT_MS}
            canInc={latencyMs < LATENCY_LIMIT_MS}
            onDec={() => setLatencyMs(latencyMs - LATENCY_STEP_MS)}
            onInc={() => setLatencyMs(latencyMs + LATENCY_STEP_MS)}
            onReset={latencyMs !== 0 ? () => setLatencyMs(0) : undefined}
          />
        </Row>
      </Group>
      <Group id="rhythm" title={strings.groupRhythm} defaultOpen={false}>
        <Row label={strings.swing} hint={!c.swingAllowed ? strings.swingUnavailable : undefined}>
          <Switch checked={Boolean(c.isSwingMode && c.swingAllowed)} disabled={c.isLoading || !c.swingAllowed || locked} onChange={c.setSwingEnabled} label={strings.swing} />
        </Row>
        <Row label="Swing feel">
          <Select value={c.swingRatioSource} disabled={!c.isSwingMode || !c.swingAllowed || c.isLoading || locked} onChange={c.setSwingRatioSource} label="Swing feel">
            <option value="auto">Auto ({autoSwingLabel})</option>
            <option value="2:1">Triplet (2:1)</option>
            <option value="5:3">Medium (5:3)</option>
            <option value="3:2">Light (3:2)</option>
          </Select>
        </Row>
      </Group>
      <Group id="section" title={strings.groupSection} defaultOpen={false}>
        {!phone && (
          <>
            <Row label={strings.hoverPlay}>
              <Switch checked={Boolean(c.hoverPlay)} disabled={c.isLoading || (locked && !c.hoverPlay)} onChange={c.setHoverPlay} label={strings.hoverPlay} />
            </Row>
            <p className="dps-hint">{c.hoverPlay ? strings.hoverPlayOn : strings.hoverPlayHint}</p>
            <div className={c.hoverPlay ? undefined : 'dps-dimmed'}>
              <Row label={strings.wholeChord} hint={strings.wholeChordHint}>
                <Switch checked={Boolean(c.wholeChord)} disabled={!c.hoverPlay} onChange={c.setWholeChord} label={strings.wholeChord} />
              </Row>
            </div>
          </>
        )}
        {!(c.selectedRange && c.selectedRange.start !== null) && <p className="dps-hint">{strings.sectionHint}</p>}
        <Row label={strings.respectRepeats}>
          <Switch checked={Boolean(c.respectRepeats)} disabled={c.isLoading || locked} onChange={c.onToggleRespectRepeats} label={strings.respectRepeats} />
        </Row>
      </Group>
    </div>
  );
};

export default PanelGroups;
